/**
 * Fiche Client Détaillée - Modal
 * Source : customerOrgs uniquement. Plus d'onglet Commandes.
 */

import React, { useEffect } from 'react';
import {
    X,
    User,
    Mail,
    Phone,
    ShoppingBag,
    TrendingUp,
    Clock,
    ExternalLink,
    DollarSign,
    Repeat,
    CalendarDays,
} from 'lucide-react';
import { TailwindDialog, DialogContent, Button } from '@/components/ui';
import { CustomerStatusDisplay } from './CustomerBadge';
import { CustomerDetailProps, PERIOD_OPTIONS } from './types';
import {
    formatCurrency,
    formatDate,
    formatPurchaseFrequency,
    derivePeriodStatusFromCustomerOrg,
    getPeriodStartDate,
} from './customerUtils';

export const CustomerDetail: React.FC<CustomerDetailProps> = ({
    customer,
    onClose,
    isOpen,
    period,
}) => {
    useEffect(() => {
        if (isOpen) {
            // Reset scroll etc. if needed
        }
    }, [customer?.id, isOpen]);

    // Les hooks doivent être appelés avant tout return conditionnel
    const periodStart = React.useMemo(() => getPeriodStartDate(period), [period]);

    // Ne pas ouvrir le modal si customer est null
    const shouldOpen = isOpen && customer !== null;

    if (!customer) return null;

    const periodStatus = derivePeriodStatusFromCustomerOrg(customer, periodStart);
    const periodLabel = PERIOD_OPTIONS.find((p) => p.value === period)?.label ?? `${period} jours`;
    const avgCart = customer.ordersPaidCount ? (customer.totalSpentCents / 100) / customer.ordersPaidCount : 0;

    return (
        <TailwindDialog
            isOpen={shouldOpen}
            onClose={onClose}
            title="Fiche Client"
            maxWidth="3xl"
            showCloseButton={false}
            variant="themed"
        >
            <DialogContent className="p-0 max-h-[80vh] overflow-hidden flex flex-col">
                <div className="bg-surface border-b border-border p-4 sm:p-6 flex-shrink-0">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-gradient-primary flex items-center justify-center text-white font-bold text-xl shadow-lg">
                                {customer.fullName.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <h2 className="text-xl sm:text-2xl font-bold text-foreground">
                                    {customer.fullName}
                                </h2>
                                <div className="flex items-center gap-3 mt-1">
                                    <CustomerStatusDisplay status={periodStatus} size="md" />
                                    <span className="text-sm text-foreground-secondary">
                                        {customer.firstPaidAt
                                            ? `Client depuis ${formatDate(customer.firstPaidAt)}`
                                            : '—'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-background-tertiary transition-colors flex-shrink-0"
                        >
                            <X className="h-5 w-5 text-foreground-secondary" />
                        </button>
                    </div>
                </div>

                <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                    <div className="space-y-6">
                        <div className="bg-primary-10 rounded-xl p-4 border border-primary-20">
                            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-4">
                                <CalendarDays className="h-4 w-4 text-primary" />
                                Période {periodLabel}
                            </h3>
                            <p className="text-sm text-foreground-secondary">
                                Statut : <strong className="text-foreground">
                                    {periodStatus === 'new' && 'Nouveau'}
                                    {periodStatus === 'existing' && 'Existant actif'}
                                    {periodStatus === 'inactive' && 'Inactif'}
                                </strong>
                            </p>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div className="p-4 rounded-xl bg-background-tertiary border border-border">
                                <div className="flex items-center gap-2 mb-2">
                                    <DollarSign className="h-4 w-4 text-success" />
                                    <span className="text-xs font-medium text-foreground-secondary">CA total</span>
                                </div>
                                <div className="text-xl font-bold text-success">
                                    {formatCurrency(customer.totalSpentCents / 100)}
                                </div>
                            </div>
                            <div className="p-4 rounded-xl bg-background-tertiary border border-border">
                                <div className="flex items-center gap-2 mb-2">
                                    <ShoppingBag className="h-4 w-4 text-primary" />
                                    <span className="text-xs font-medium text-foreground-secondary">Commandes payées</span>
                                </div>
                                <div className="text-xl font-bold text-primary">
                                    {customer.ordersPaidCount}
                                </div>
                            </div>
                            <div className="p-4 rounded-xl bg-background-tertiary border border-border">
                                <div className="flex items-center gap-2 mb-2">
                                    <TrendingUp className="h-4 w-4 text-info" />
                                    <span className="text-xs font-medium text-foreground-secondary">Panier moyen</span>
                                </div>
                                <div className="text-xl font-bold text-info">
                                    {formatCurrency(avgCart)}
                                </div>
                            </div>
                            <div className="p-4 rounded-xl bg-background-tertiary border border-border">
                                <div className="flex items-center gap-2 mb-2">
                                    <Repeat className="h-4 w-4 text-warning" />
                                    <span className="text-xs font-medium text-foreground-secondary">Fréquence</span>
                                </div>
                                <div className="text-xl font-bold text-warning">
                                    {formatPurchaseFrequency(customer.avgDaysBetweenPurchaseDays || undefined)}
                                </div>
                            </div>
                        </div>

                        {(customer.refundsCount > 0 || customer.refundsCents > 0) && (
                            <div className="p-4 rounded-xl bg-background-tertiary border border-border">
                                <h3 className="text-sm font-semibold text-foreground mb-2">Remboursements</h3>
                                <p className="text-sm text-foreground-secondary">
                                    {customer.refundsCount} remboursement{customer.refundsCount > 1 ? 's' : ''} · {formatCurrency(customer.refundsCents / 100)}
                                </p>
                            </div>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-background-tertiary rounded-xl p-4 border border-border">
                                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                                    <User className="h-4 w-4 text-primary" />
                                    Coordonnées
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <Mail className="h-4 w-4 text-foreground-secondary" />
                                        <a href={`mailto:${customer.email}`} className="text-sm text-primary hover:underline">
                                            {customer.email}
                                        </a>
                                    </div>
                                    {customer.phone && (
                                        <div className="flex items-center gap-3">
                                            <Phone className="h-4 w-4 text-foreground-secondary" />
                                            <a href={`tel:${customer.phone}`} className="text-sm text-foreground hover:text-primary">
                                                {customer.phone}
                                            </a>
                                        </div>
                                    )}
                                    {customer.tiktokUsername && (
                                        <div className="flex items-center gap-3">
                                            <svg className="h-4 w-4 text-foreground-secondary" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43V7.56a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04.01z" />
                                            </svg>
                                            <a
                                                href={`https://tiktok.com/@${customer.tiktokUsername}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-foreground hover:text-primary flex items-center gap-1"
                                            >
                                                @{customer.tiktokUsername}
                                                <ExternalLink className="h-3 w-3" />
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="bg-background-tertiary rounded-xl p-4 border border-border">
                                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-primary" />
                                    Dates
                                </h3>
                                <div className="space-y-2 text-sm">
                                    <p>
                                        <span className="text-foreground-secondary">Première commande :</span>{' '}
                                        <span className="text-foreground font-medium">
                                            {customer.firstPaidAt ? formatDate(customer.firstPaidAt) : '—'}
                                        </span>
                                    </p>
                                    <p>
                                        <span className="text-foreground-secondary">Dernière commande :</span>{' '}
                                        <span className="text-foreground font-medium">
                                            {customer.lastPaidAt ? formatDate(customer.lastPaidAt) : '—'}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-surface border-t border-border p-4 sm:p-6 flex-shrink-0">
                    <div className="flex flex-col sm:flex-row gap-3 justify-end">
                        <Button variant="outline" onClick={onClose}>
                            Fermer
                        </Button>
                        <Button variant="primary" onClick={() => { globalThis.location.href = `mailto:${customer.email}`; }} leftIcon={<Mail className="h-4 w-4" />}>
                            Contacter
                        </Button>
                        {customer.tiktokUsername && (
                            <Button
                                variant="secondary"
                                onClick={() => { globalThis.open(`https://tiktok.com/@${customer.tiktokUsername}`, '_blank'); }}
                                leftIcon={<ExternalLink className="h-4 w-4" />}
                            >
                                TikTok
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </TailwindDialog>
    );
};
